import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit, inject } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import {
    EventParticipant,
    EventParticipantType,
    ParticipantInfo,
    getPitcherByParticipantType,
    getRankByParticipantType,
    getRedCards,
    getWinLossStatsByParticipantType,
} from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { EpcotConfig, GridConfig, Sitecore, StylingGridConfiguration } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { InlineTooltipComponent } from '@frontend/sports/tooltip-feature';
import { isEmpty } from 'lodash-es';

import { FixtureFormComponent } from './form-indicator/fixture-form.component';

@Component({
    selector: 'ms-event-name',
    templateUrl: './event-name.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, InlineTooltipComponent, ParticipantImageComponent, FixtureFormComponent, AutomationDirective],
})
export class EventNameComponent implements OnChanges, OnInit {
    sitecore = inject(Sitecore);
    private shortTeamNamesService = inject(ShortTeamNamesService);
    private stylingGridConfig = inject(StylingGridConfiguration);
    private epcotConfig = inject(EpcotConfig);
    private statisticsConfigService = inject(StatisticsConfigService);
    private gridConfig = inject(GridConfig);

    @Input() event: GridEvent;
    @Input() showParticipantImage: boolean;
    @Input() showParticipantStats = false;
    @Input() showLogoColor: boolean;
    @HostBinding('class') className = 'grid-event-name';
    enableImages: boolean;
    participantInfos: ParticipantInfo[];
    teamRecordsEnabled = false;
    teamRanksEnabled = false;
    teamPitchersEnabled = false;
    showRedCardsInSoccer = false;

    ngOnInit(): void {
        this.showRedCardsInSoccer = this.shouldShowRedCardsInSoccer();
    }

    ngOnChanges(changes: ISimpleChanges<EventNameComponent>): void {
        if (changes.showParticipantImage && changes.showParticipantImage.currentValue) {
            this.enableImages =
                this.showParticipantImage &&
                (this.event.participants.some((x) => x.image) || this.epcotConfig.isEnabled) &&
                (this.event.participants.some((x) => x.image?.logo) ||
                    !this.stylingGridConfig.participantImageSportBlacklist.includes(this.event.sport.id));
        }
        if (changes.event) {
            if (this.showParticipantStats) {
                this.teamPitchersEnabled = !!this.statisticsConfigService.pitcherEnabledSports[this.event.sport.id];
                this.teamRecordsEnabled = !isEmpty(this.statisticsConfigService.teamRecordsFormat[this.event.sport.id]);
                this.teamRanksEnabled = !!this.statisticsConfigService.teamRankEnabledSports[this.event.sport.id];
            }

            this.showRedCardsInSoccer = this.shouldShowRedCardsInSoccer();

            this.setParticipantInfos();
        }
    }

    get view(): string {
        if (this.event.league.combiLeague) {
            return 'Combi';
        } else if (this.event.participants.length === 2 && !this.event.isTournamentGame()) {
            return 'Pair';
        } else if (!this.event.isTournamentGame() && this.event.isLeaderboardGame()) {
            return 'LeaderboardGame';
        }

        return 'Tournament';
    }

    get option(): string | undefined {
        const optionGroup = this.event.optionGroups && this.event.optionGroups[0];

        if (!optionGroup) {
            return '';
        }

        const singleOption = optionGroup.options && optionGroup.options.length === 1;

        return singleOption ? optionGroup.options[0].name : optionGroup.name;
    }

    get leader(): boolean {
        if (!this.event.isTournamentGame() || !this.event.scoreboard || this.event.scoreboard.isGeneralTournament) {
            return false;
        }

        return this.event.scoreboard.started && this.event.participants.length > 0;
    }

    private setParticipantInfos(): void {
        this.participantInfos = this.event.participants.map((participant, index) => {
            const participantNames = this.getParticipantName(participant);
            const rank = this.teamRanksEnabled ? getRankByParticipantType(this.event, participant.type) : undefined;
            const pitcher = this.teamPitchersEnabled ? getPitcherByParticipantType(this.event, participant.type) : undefined;
            const teamRecordsFormat = this.statisticsConfigService.teamRecordsFormat[this.event.sport.id];
            const winLossStats =
                !pitcher && teamRecordsFormat && this.teamRecordsEnabled
                    ? getWinLossStatsByParticipantType(this.event, participant.type!, teamRecordsFormat)
                    : undefined;

            return {
                participant,
                participantNames,
                isSingleContestant: participantNames.length === 1,
                rank,
                winLossStats,
                pitcher,
                redCards: this.getParticipantRedCards(index),
            } as ParticipantInfo;
        });
    }

    private getParticipantName(participant: EventParticipant): string[] {
        let participantName = [];
        if (this.shortTeamNamesService.shouldUseShortName(participant, this.event.sport.id, this.event.league.id, ShortNameModule.Grids)) {
            const name = participant.shortName!;
            participantName.push(this.shortTeamNamesService.clearShortName(name));
        } else {
            participantName.push(participant.name);
        }
        //this will display double participants in stack view if it is epcot and Tennis/Tabletennis
        if (this.epcotConfig.isEnabled && this.gridConfig.is2way3wayEnabled) {
            participantName = this.getDoublesParticipantName(participantName[0]);
        }

        return participantName;
    }

    private getDoublesParticipantName(participantName: string): string[] {
        if ((this.event.sport.id === SportConstant.Tennis || this.event.sport.id === SportConstant.TableTennis) && participantName.includes('/')) {
            return participantName.split('/');
        }

        return new Array(participantName);
    }

    private getParticipantRedCards(participantIndex: number): number[] {
        const redCards = getRedCards(this.event, true, true) || [];

        return redCards.length > 0 ? new Array(redCards[participantIndex]) : [];
    }

    private shouldShowRedCardsInSoccer(): boolean {
        return this.gridConfig.showRedCardsInSoccer && this.event.live && this.event.sport.id === SportConstant.Soccer && this.epcotConfig.isEnabled;
    }

    showAwayIndicator(participant: EventParticipant): boolean {
        return !this.epcotConfig.isEnabled && this.event.viewType === FixtureViewType.American && participant.type === EventParticipantType.Away;
    }

    shouldHighlight(participant: EventParticipant): boolean {
        return participant.type !== undefined && participant.type === this.event.getBestPerformingParticipant();
    }
}
